/**
 * PageTitleContext - Provides a way for pages to set their title
 * which will be displayed in the Layout header instead of within the page content.
 */
import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';

interface PageTitleContextType {
  title: string;
  mobileTitle: string;
  headerTabs: ReactNode | null;
  setPageTitle: (title: string, mobileTitle?: string) => void;
  clearPageTitle: () => void;
  setHeaderTabs: (tabs: ReactNode | null) => void;
}

const PageTitleContext = createContext<PageTitleContextType | undefined>(undefined);

interface PageTitleProviderProps {
  children: ReactNode;
}

export function PageTitleProvider({ children }: PageTitleProviderProps) {
  const [title, setTitle] = useState('');
  const [mobileTitle, setMobileTitle] = useState('');
  const [headerTabs, setHeaderTabsState] = useState<ReactNode | null>(null);

  const setPageTitle = useCallback((newTitle: string, newMobileTitle?: string) => {
    setTitle(newTitle);
    setMobileTitle(newMobileTitle || newTitle);
  }, []);

  const clearPageTitle = useCallback(() => {
    setTitle('');
    setMobileTitle('');
  }, []);

  const setHeaderTabs = useCallback((tabs: ReactNode | null) => {
    setHeaderTabsState(tabs);
  }, []);

  const contextValue: PageTitleContextType = {
    title,
    mobileTitle,
    headerTabs,
    setPageTitle,
    clearPageTitle,
    setHeaderTabs,
  };

  return (
    <PageTitleContext.Provider value={contextValue}>
      {children}
    </PageTitleContext.Provider>
  );
}

/**
 * Hook to access page title context
 */
export function usePageTitleContext() {
  const context = useContext(PageTitleContext);
  if (context === undefined) {
    throw new Error('usePageTitleContext must be used within a PageTitleProvider');
  }
  return context;
}

/**
 * Hook for pages to set their title on mount and clear on unmount.
 * This is the primary hook pages should use.
 *
 * @param title - The page title (already translated)
 */
export function usePageTitle(title: string, mobileTitle?: string) {
  const { setPageTitle, clearPageTitle } = usePageTitleContext();

  useEffect(() => {
    setPageTitle(title, mobileTitle);

    return () => {
      clearPageTitle();
    };
  }, [title, mobileTitle, setPageTitle, clearPageTitle]);
}
